<template>
  <CustomModal
    v-model:visible="internalVisible"
    title="Generate Token"
    width="480px"
    :source="`${source}_generate_token_modal`"
  >
    <div v-if="!generatedToken" class="flex flex-col gap-6">
      <div id="token_name" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Token Name</span>

        <a-input
          v-model:value="formData.name"
          placeholder="Enter token name"
          class="input_medium"
        />
      </div>

      <div id="expiry_date" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Expiry Date</span>

        <a-date-picker
          v-model:value="formData.expiryDate"
          placeholder="Select expiry date"
          :disabled-date="disabledDate"
        />
      </div>
    </div>

    <div v-else class="flex flex-col gap-6">
      <div id="generated_token" class="flex flex-col gap-1">
        <span class="text-[13px] secondary_color">Generated Token</span>

        <div
          class="flex text-sm font-normal py-2 px-3 rounded-lg break-all space_mono border_8"
        >
          {{ generatedToken }}
        </div>
      </div>

      <CustomAlerts
        :source="`${source}_generate_token_modal`"
        type="info"
        description="This token will be shown only once now. If you lose it, you will have to generate a new token."
      />
    </div>

    <template #footer>
      <div class="flex gap-2 justify-end">
        <template v-if="!generatedToken">
          <a-button class="secondary_medium" @click="handleCancel">
            Cancel
          </a-button>

          <a-button
            class="primary_medium"
            :loading="generating"
            :disabled="!canGenerate"
            @click="handleGenerate"
          >
            {{ generating ? "Generating.." : "Generate API Key" }}
          </a-button>
        </template>

        <a-button v-else class="primary_medium" @click="handleCopyToken">
          Copy Token
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, reactive, computed, watch } from "vue";
import { message } from "ant-design-vue";
import { copyToClipboard } from "@/src/utils/utils.js";
import codebaseService from "@/src/api/CodeService.js";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomAlerts from "@/src/components/CustomAlerts.vue";

const props = defineProps({
  source: { type: String, default: "" },
  visible: { type: Boolean, required: true },
});

const emit = defineEmits(["update:visible", "created"]);

const internalVisible = ref(props.visible);
const formData = reactive({ name: "", expiryDate: null });
const generating = ref(false);
const generatedToken = ref("");

const canGenerate = computed(
  () => formData.name?.trim() && formData.expiryDate,
);

const handleCopyToken = () => {
  copyToClipboard(generatedToken.value, "Token copied to clipboard");
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
  } catch (error) {
    console.error("An error occurred while handling cancel: ", error);
  }
};

const resetForm = () => {
  try {
    formData.name = "";
    formData.expiryDate = null;
    generatedToken.value = "";
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const disabledDate = (current) => {
  try {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return current && current.toDate() < today;
  } catch (error) {
    console.error(
      "An error occurred while calculating the disabled date: ",
      error,
    );
  }
};

const handleGenerate = async () => {
  try {
    generating.value = true;
    const payload = {
      name: formData.name.trim(),
      expires_at: formData.expiryDate?.toISOString(),
    };
    const response = await codebaseService.base.tokens.create(payload);
    if (response?.success) {
      generatedToken.value = response.data?.token || "";
      emit("created");
    }
  } catch (error) {
    console.error("An error occurred while generating token:", error);
    message.error(error?.response?.data?.error || "Failed to generate token");
  } finally {
    generating.value = false;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    internalVisible.value = newVal;
  },
);

watch(internalVisible, (newVal) => {
  if (!newVal) {
    resetForm();
    emit("update:visible", false);
  }
});
</script>
